<template>
  <Modal @hide="reset">
    <template #activator="{ show }">
      <slot :show="show" />
    </template>
    <template #header="{ hide }">
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-2 text-blue-800 items-center">
          <EvidenceLogo class="w-7" />
          <p class="font-semibold">International HTA Database</p>
        </div>
        <button class="p-2" @click="hide">
          <XMarkIcon class="w-6 text-slate-400 hover:text-slate-600" />
        </button>
      </div>
    </template>
    <template #content>
      <div
        class="w-[980px] create-review-modal flex flex-col rounded-2xl relative"
      >
        <div
          v-if="isSearching"
          class="overflow-hidden absolute top-0 left-0 w-full h-1"
        >
          <div class="line absolute bg-primary-500/20 h-1"></div>
          <div class="absolute bg-primary-500 h-1 inc"></div>
          <div class="absolute bg-primary-500 h-1 dec"></div>
        </div>
        <div class="px-6 mt-2">
          <div class="flex gap-2 items-end">
            <TextInput
              v-model="technique"
              label="Technique"
              placeholder="Enter technique name"
              class="flex-1"
              :input-classes="'px-3 py-1'"
              :disabled="isSearching"
              @enter="onEnter"
            />
          </div>
        </div>
        <div class="relative">
          <div class="mx-6 my-4 bg-white rounded-lg space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <DatePicker
                v-model="startDate"
                placeholder="Start Year"
                class="flex-1"
                year-picker
              />
              <DatePicker
                v-model="endDate"
                placeholder="End Year"
                class="flex-1"
                year-picker
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer="{ hide }">
      <div class="p-6 flex justify-between items-center w-full">
        <div />
        <div>
          <button
            class="truncate flex flex-row gap-2 bg-blue-800 text-white items-center py-2 px-4 rounded-md text-base font-medium"
            :class="{
              '!bg-slate-100 !text-black/50': !canImport,
            }"
            :disabled="!canImport || isImporting || isSearching"
            @click="() => importHtaSearch().then(() => hide())"
          >
            <ImportIcon
              v-if="!isImporting"
              class="w-5 h-5 text-white"
              :class="{ '!text-black/50': !canImport }"
            />
            <span v-if="isImporting"> <Spinner color="white" /> </span>
            <span v-else>
              Import {{ totalResults > -1 ? totalResults : 0 }} results
            </span>
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import TextInput from '@app/components/Global/Inputs/TextInput.vue'
import DatePicker from '@app/components/Global/Inputs/DatePicker.vue'
import { computed, ref } from 'vue'
import { SnackbarState } from '@app/types'
import useSnackbar from '@app/composables/use-snackbar'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { ImportSourceType } from '@core/domain/types/import-source-type.type'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import Spinner from '@app/components/Global/Spinner.vue'
import axios from 'axios'
import ImportIcon from '@app/components/Icons/ImportIcon.vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import Modal from '@app/components/Global/Modal/Modal.vue'

import { Review } from '@core/domain/models/review.model'
import { FieldSafetyNoticeMetadata } from '@core/domain/models/reviewItemMetadata/fieldSafetyNoticeMetadata'
import { Prettify } from '@app/utils/prettifyType'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import useLoading from '@app/composables/use-loading'
import EvidenceLogo from '@app/components/Icons/EvidenceLogo.vue'
import { watchDebounced } from '@vueuse/core'
import { HtaApiClient } from '@infrastructure/htaApi/htaApi.client'
import { HtaMetadata } from '@core/domain/models/reviewItemMetadata/htaMetadata'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import useBuiltInImportSources from '@app/composables/use-built-in-import-sources'

const htaApiClient = new HtaApiClient()

const builtInImportSources = useBuiltInImportSources()

const review = injectStrict(ReviewKey)
const snackbar = useSnackbar()

const results = ref<FieldSafetyNoticeMetadata[]>([])
const currentPage = ref(1)
const totalPages = ref(1)
const limit = ref(12)
const totalResults = ref(-1)

const isSearching = ref(false)
const isImporting = ref(false)
const loading = useLoading()

const technique = ref('')
const startDate = ref('')
const endDate = ref('')

const query = computed(() => {
  return technique.value.trim()
})

const canImport = computed(() => {
  return (
    review.entity.value?.plan?.lockState === ReviewLockState.UNLOCKED &&
    query.value !== ''
  )
})

async function importHtaSearch() {
  loading.start()

  try {
    const searchResults = await htaApiClient.search(
      query.value,
      startDate.value,
      endDate.value,
    )

    let url = `https://database.inahta.org/search?limit=&terms=${encodeURIComponent(query.value)}&client=user`

    if (startDate.value) {
      url += `&filter-year-from=${startDate.value}`
    }
    if (endDate.value) {
      url += `&filter-year-to=${endDate.value}`
    }

    const filters: string[] = []
    if (startDate.value && endDate.value) {
      filters.push(`Year: from ${startDate.value} to ${endDate.value}`)
    } else if (startDate.value) {
      filters.push(`Year: from ${startDate.value}`)
    } else if (endDate.value) {
      filters.push(`Year: to ${endDate.value}`)
    }

    const search: {
      query?: string
      filters?: string
      date: string
      reviewId: number
      importSourceId: string
      items: Prettify<HtaMetadata & { type: ReviewItemType.Hta }>[]
      url?: string
    } = {
      query: query.value,
      filters: filters.length > 0 ? filters.join(', ') : '',
      date: new Date().toISOString(),
      reviewId: review.entity.value.id,
      importSourceId: BuiltInImportSourceId.HTA_DATABASE,
      items: searchResults.map((item) => ({
        type: ReviewItemType.Hta,
        abstract: item.abstract,
        title: item.title,
        authors: item.authors,
        doi: item.doi,
        issueNumber: item.issueNumber,
        journal: item.journal,
        pagesNumber: item.pagesNumber,
        potentialPdfUrl: item.potentialPdfUrl,
        publishYear: item.publishYear,
      })),
      url,
    }

    if (!sourceExistsInPlan(search.importSourceId, review.entity.value)) {
      const source = builtInImportSources.findById(
        BuiltInImportSourceId.HTA_DATABASE,
      )

      await review.addImportSourceToPlan({
        name: source.name,
        type: ImportSourceType.FIELD_SAFETY_NOTICES,
        url: source.url ?? '',
        id: source.id,
        description: source.description,
      })
    }

    await review.importSearch(search)
    snackbar.success('Success')
    reset()
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 500
    ) {
      snackbar.show(
        SnackbarState.ERROR,
        'Internal Server Error. Please try again.',
      )
    } else {
      snackbar.show(SnackbarState.ERROR, 'Failed to import hta.')
    }
    console.error('Error importing hta:', error)
  } finally {
    loading.stop()
  }
}

function sourceExistsInPlan(importSourceId: string, review: Review): boolean {
  const enabledSources = review.plan?.importPlan.importSources || []
  return enabledSources.some((source) => source.id === importSourceId)
}

async function search() {
  if (!query.value) {
    return
  }
  isSearching.value = true
  try {
    const htaRecords = await htaApiClient.search(
      query.value,
      startDate.value,
      endDate.value,
    )

    if (htaRecords.length) {
      results.value = htaRecords
      totalResults.value = htaRecords.length
      totalPages.value = Math.ceil(totalResults.value / limit.value)
    } else {
      results.value = []
      totalResults.value = 0
      totalPages.value = 1
    }
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 500
    ) {
      snackbar.show(
        SnackbarState.ERROR,
        'Internal Server Error. Please try again.',
      )
    }
    console.error('Error fetching hta:', error)
    results.value = []
    totalResults.value = 0
    totalPages.value = 1
  } finally {
    isSearching.value = false
  }
}

function onEnter() {
  search()
}

watchDebounced(
  () => [startDate.value, endDate.value, query.value],
  () => {
    results.value = []
    currentPage.value = 1
    totalPages.value = 1
    totalResults.value = -1
    search()
  },
  {
    debounce: 1000,
  },
)

function reset() {
  results.value = []
  currentPage.value = 1
  totalPages.value = 1
  totalResults.value = -1
  startDate.value = ''
  endDate.value = ''

  technique.value = ''
}
</script>
